import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MovieHero from './components/MovieHero';
import CastSection from './components/CastSection';
import ReviewsSection from './components/ReviewsSection';
import RelatedSection from './components/RelatedSection';
import MoviePaymentModal from '../movies/components/MoviePaymentModal';
import { movies } from '../../mocks/movies';
import { getMovieDetail } from '../../mocks/movieDetails';

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showPayment, setShowPayment] = useState(false);

  const movie = movies.find(m => m.id === id);
  if (!movie) return <Navigate to="/movies" replace />;

  const detail = getMovieDetail(movie.id, `bd_${movie.id}`);

  const typeLabel =
    movie.type === 'movie' ? 'فيلم' :
    movie.type === 'series' ? 'مسلسل' : 'رسوم متحركة';

  return (
    <main dir="rtl" className="bg-[#F5F1E8] min-h-screen">
      <Navbar />

      {/* Hero — full bleed above navbar */}
      <div className="mt-0 pt-20">
        <MovieHero movie={movie} detail={detail} />
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <Link to="/" className="hover:text-[#C9A961] transition-colors cursor-pointer">الرئيسية</Link>
          <i className="ri-arrow-left-s-line" />
          <Link to="/movies" className="hover:text-[#C9A961] transition-colors cursor-pointer">أفلام ومسلسلات</Link>
          <i className="ri-arrow-left-s-line" />
          <span className="text-gray-700 font-bold">{movie.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* ── Main content ── */}
          <div className="lg:col-span-2 space-y-10">

            {/* Synopsis */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-file-text-line text-[#C9A961]" />
                </div>
                قصة العمل
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm">{detail.synopsis}</p>
            </div>

            {/* Cast */}
            <div className="bg-white rounded-2xl p-6">
              <CastSection
                cast={detail.cast}
                director={detail.director}
                studio={detail.studio}
                awards={detail.awards}
              />
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-2xl p-6">
              <ReviewsSection reviews={detail.reviews} overallRating={movie.rating} />
            </div>

          </div>

          {/* ── Sidebar ── */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">

              {/* Poster card */}
              <div className="bg-white rounded-2xl overflow-hidden">
                <div className="relative" style={{ paddingBottom: '145%' }}>
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  {movie.badge && (
                    <div className="absolute top-3 right-3 bg-[#C9A961] text-white text-xs font-black px-2 py-1 rounded-lg">
                      {movie.badge}
                    </div>
                  )}
                </div>

                <div className="p-5">
                  {/* Price */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">سعر المشاهدة</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black text-[#C9A961]">25</span>
                        <span className="text-sm font-bold text-gray-500">درهم</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-[#C9A961]/10 px-3 py-1.5 rounded-full">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-[#C9A961] text-sm" />
                      </div>
                      <span className="text-[#C9A961] font-black text-sm">{movie.rating}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowPayment(true)}
                    className="w-full flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-[#b8922e] text-white py-4 rounded-xl font-black cursor-pointer transition-colors whitespace-nowrap mb-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-circle-fill text-xl" />
                    </div>
                    شاهد الآن
                  </button>

                  {/* Info list */}
                  <div className="space-y-2.5 pt-4 border-t border-gray-100">
                    {[
                      { label: 'النوع', value: typeLabel, icon: 'ri-movie-line' },
                      { label: 'سنة الإنتاج', value: String(movie.year), icon: 'ri-calendar-line' },
                      { label: 'المدة', value: movie.duration, icon: 'ri-time-line' },
                      { label: 'اللغة', value: movie.language, icon: 'ri-translate-2' },
                      { label: 'الدولة', value: detail.country, icon: 'ri-map-pin-line' },
                      { label: 'التصنيف العمري', value: detail.ageRating, icon: 'ri-user-line' },
                    ].map(item => (
                      <div key={item.label} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-400">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${item.icon} text-xs`} />
                          </div>
                          {item.label}
                        </div>
                        <span className="font-bold text-gray-700">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Genres card */}
              <div className="bg-white rounded-2xl p-5">
                <p className="text-xs font-black text-gray-500 mb-3">التصنيفات</p>
                <div className="flex flex-wrap gap-2">
                  {movie.genre.map(g => (
                    <Link
                      key={g}
                      to="/movies"
                      className="text-xs font-bold bg-[#F5F1E8] text-[#C9A961] px-3 py-1.5 rounded-full hover:bg-[#C9A961] hover:text-white transition-colors cursor-pointer"
                    >
                      {g}
                    </Link>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Related movies */}
        <RelatedSection current={movie} all={movies} />

      </div>

      <Footer />

      {showPayment && (
        <MoviePaymentModal movie={movie} onClose={() => setShowPayment(false)} />
      )}
    </main>
  );
}
